import Avatar from '@/components/base/Avatar';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import {
  auditCategoryTone,
  auditImpactTone,
  auditOutcomeTone,
  clockOf,
  dayKeyOf,
} from '@/utils/audit';
import type { AuditEvent } from '@/types/portal';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function longStamp(timestamp: string): string {
  const [datePart, timePart] = timestamp.split(' ');
  const [y, m, d] = datePart.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}, ${timePart}`;
}

interface AuditDetailModalProps {
  event: AuditEvent | null;
  onClose: () => void;
}

export default function AuditDetailModal({ event, onClose }: AuditDetailModalProps) {
  return (
    <Modal
      open={event !== null}
      onClose={onClose}
      icon="ri-history-line"
      title={event?.action ?? 'Audit event'}
      subtitle={event ? `${longStamp(event.timestamp)} · ${event.reference}` : undefined}
      widthClassName="max-w-2xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      {event ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={auditCategoryTone(event.category)}>{event.category}</Badge>
            <Badge tone={auditImpactTone(event.impact)} icon="ri-alert-line">
              {event.impact}
            </Badge>
            <Badge tone={auditOutcomeTone(event.outcome)}>{event.outcome}</Badge>
            <Badge tone="neutral" icon="ri-map-pin-line">
              {event.market}
            </Badge>
          </div>

          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3.5">
            <Avatar src={event.actorAvatar} alt={event.actorName} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-950">{event.actorName}</p>
              <p className="text-xs text-foreground-500">{event.actorRole}</p>
            </div>
            <span className="ml-auto flex h-8 w-8 items-center justify-center rounded-md bg-background-200 text-base text-foreground-600">
              <i className="ri-shield-user-line" />
            </span>
          </div>

          <p className="text-sm leading-relaxed text-foreground-700">
            <span className="font-medium text-foreground-950">{event.actorName}</span>{' '}
            {event.summary}.
          </p>

          {event.changes.length > 0 ? (
            <div>
              <h4 className="mb-2 font-heading text-xs font-semibold uppercase tracking-wider text-foreground-400">
                What changed
              </h4>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <table className="row-accent w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-100 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                      <th className="px-4 py-2 font-semibold">Field</th>
                      <th className="px-4 py-2 font-semibold">Before</th>
                      <th className="px-4 py-2 font-semibold">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.changes.map((change) => (
                      <tr key={change.field} className="border-b border-background-100 last:border-0">
                        <td className="px-4 py-2.5 text-foreground-700">{change.field}</td>
                        <td className="px-4 py-2.5 text-foreground-500">{change.before}</td>
                        <td className="px-4 py-2.5 font-medium text-foreground-950">{change.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div>
            <h4 className="mb-2 font-heading text-xs font-semibold uppercase tracking-wider text-foreground-400">
              Event metadata
            </h4>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-background-200 bg-background-50 p-3">
                <dt className="text-[11px] uppercase tracking-wider text-foreground-400">Resource</dt>
                <dd className="mt-0.5 text-sm text-foreground-900">
                  {event.resource} · {event.resourceId}
                </dd>
              </div>
              <div className="rounded-md border border-background-200 bg-background-50 p-3">
                <dt className="text-[11px] uppercase tracking-wider text-foreground-400">Reference</dt>
                <dd className="mt-0.5 text-sm text-foreground-900">{event.reference}</dd>
              </div>
              <div className="rounded-md border border-background-200 bg-background-50 p-3">
                <dt className="text-[11px] uppercase tracking-wider text-foreground-400">Source</dt>
                <dd className="mt-0.5 text-sm text-foreground-900">{event.source}</dd>
              </div>
              <div className="rounded-md border border-background-200 bg-background-50 p-3">
                <dt className="text-[11px] uppercase tracking-wider text-foreground-400">IP address</dt>
                <dd className="mt-0.5 text-sm tabular-nums text-foreground-900">{event.ip}</dd>
              </div>
              <div className="rounded-md border border-background-200 bg-background-50 p-3">
                <dt className="text-[11px] uppercase tracking-wider text-foreground-400">Recorded</dt>
                <dd className="mt-0.5 text-sm text-foreground-900">
                  {dayKeyOf(event.timestamp)} · {clockOf(event.timestamp)}
                </dd>
              </div>
              <div className="rounded-md border border-background-200 bg-background-50 p-3">
                <dt className="text-[11px] uppercase tracking-wider text-foreground-400">Event ID</dt>
                <dd className="mt-0.5 text-sm tabular-nums text-foreground-900">{event.id}</dd>
              </div>
            </dl>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}